'use client'
import * as React from 'react'
import { usePathname } from 'next/navigation'
import { trackScrollDepth, trackOutbound } from '@/lib/analytics/ga4'

// Global GA4 listeners, mounted once in the root layout.
//
// Responsibilities:
//   1. Scroll-depth: fires scroll_depth events at 25 / 50 / 75 / 100% of the
//      current page. Resets on every route change so each page records its
//      own funnel.
//   2. Outbound links: a single document-level click listener catches every
//      external anchor (different hostname) and fires outbound_link before
//      the navigation. Captures the surface from the closest [data-surface]
//      ancestor when present so reports can slice by section.

const DEPTH_STEPS: Array<25 | 50 | 75 | 100> = [25, 50, 75, 100]

export function AnalyticsListeners() {
  const pathname = usePathname()
  const firedRef = React.useRef<Set<number>>(new Set())

  // Reset scroll-depth tracking on every route change.
  React.useEffect(() => {
    firedRef.current = new Set<number>()
  }, [pathname])

  // Scroll depth.
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    let raf = 0
    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const doc = document.documentElement
        const max = doc.scrollHeight - doc.clientHeight
        if (max <= 0) return
        const pct = Math.min(100, Math.round((window.scrollY / max) * 100))
        for (const step of DEPTH_STEPS) {
          if (pct >= step && !firedRef.current.has(step)) {
            firedRef.current.add(step)
            trackScrollDepth(step)
          }
        }
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(raf)
    }
  }, [pathname])

  // Outbound link clicks.
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null
      if (!anchor) return
      const href = anchor.getAttribute('href') || ''
      // Skip relative links, anchors, mailto/tel
      if (!/^https?:\/\//i.test(href)) return
      try {
        const u = new URL(href, window.location.href)
        if (u.hostname === window.location.hostname) return
        const surface = anchor.closest('[data-surface]')?.getAttribute('data-surface') || 'unknown'
        trackOutbound({ href: u.toString(), surface })
      } catch { /* ignore */ }
    }
    document.addEventListener('click', handler, true)
    return () => document.removeEventListener('click', handler, true)
  }, [])

  return null
}
